import { useQuery } from "@tanstack/react-query";
import { isGuestOrNoWallet } from "@shared/domain/guestSession";
import type { AppNavigationTarget } from "../../appNavTypes";
import { fetchTrainerProfile } from "../../api/petBackendApi";
import { TrainerAvatar } from "../TrainerAvatar";
import { useT } from "../../i18n/i18nContext";

interface GameProfileCardProps {
  /** Acting identity / guest sentinel address, or null when signed out. */
  actingAddress: string | null;
  onNavigate: (target: AppNavigationTarget) => void;
}

/**
 * Avatar button at the foot of the rail. Clicking it opens the player's profile page (trainer
 * control, collection, transactions). The narrow rail shows the avatar only.
 */
export function GameProfileCard({ actingAddress, onNavigate }: GameProfileCardProps) {
  const isGuest = isGuestOrNoWallet(actingAddress);
  const t = useT();

  const { data: profile } = useQuery({
    queryKey: ["trainerProfile", actingAddress],
    queryFn: () => fetchTrainerProfile(actingAddress as string),
    enabled: !!actingAddress && !isGuest,
    staleTime: 60_000,
  });

  return (
    <button
      type="button"
      className="game-profile-card"
      aria-label={t("navigation.game.yourProfile")}
      onClick={() => onNavigate("/profile")}
    >
      <span className="game-profile-card__avatar">
        <TrainerAvatar avatar={profile?.avatar ?? null} size={40} />
      </span>
    </button>
  );
}
